import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ArrowUpRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { CONTACT, IMAGES } from "@/data";

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.35 + i * 0.12, ease: [0.16, 1, 0.3, 1] },
  }),
};

export const Hero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
  const overlayY = useTransform(scrollYProgress, [0, 1], ["0%", "-12%"]);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section ref={ref} className="relative min-h-[100svh] flex items-end overflow-hidden noise-overlay">
      <motion.div className="absolute inset-0 z-0" style={{ y: imgY, scale: imgScale }}>
        <video
          className="h-full w-full object-cover"
          src="/hero-renovation.mp4"
          poster={IMAGES.heroPrimary}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          aria-hidden="true"
          data-testid="hero-video"
        />
      </motion.div>
      <div className="absolute inset-0 z-10 bg-gradient-to-t from-[#0B132B] via-[#0B132B]/75 to-[#0B132B]/55" />
      <div className="absolute inset-0 z-10 bg-[#0B132B]/45" />

      <motion.div style={{ y: overlayY }} className="relative z-20 container-x pb-16 sm:pb-24 pt-32 w-full">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15 }}
        >
          <span className="tag-label" data-testid="hero-badge">{t.hero.badge}</span>
        </motion.div>

        <h1 className="font-display font-extrabold text-[clamp(2.4rem,6.2vw,5.25rem)] leading-[0.98] mt-6 max-w-5xl">
          {t.hero.titleLines.map((line, i) => (
            <span key={i} className="mask-line">
              <motion.span
                className="block"
                custom={i}
                variants={lineVariants}
                initial="hidden"
                animate="visible"
              >
                {i === t.hero.titleLines.length - 1 ? (
                  <span className="text-[#FF8C42]">{line}</span>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.9 }}
          className="text-[#C7CCD6] text-base sm:text-lg max-w-xl mt-7 leading-relaxed"
        >
          {t.hero.desc}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 1.05 }}
          className="flex flex-wrap items-center gap-4 mt-9"
        >
          <a
            data-testid="hero-call-btn"
            href={`tel:${CONTACT.phones[0].tel}`}
            className="group inline-flex items-center gap-2.5 rounded-full bg-[#E85D04] hover:bg-[#D05303] px-7 py-4 font-bold text-white transition-colors"
          >
            <Phone className="h-5 w-5" />
            {t.hero.call}
          </a>
          <button
            data-testid="hero-quote-btn"
            onClick={() => scrollTo("contact")}
            className="group inline-flex items-center gap-2.5 rounded-full border border-white/25 hover:border-[#FF8C42] hover:bg-white/5 px-7 py-4 font-bold text-white transition-colors"
          >
            {t.hero.quote}
            <ArrowUpRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.3 }}
          className="flex flex-wrap gap-8 sm:gap-14 mt-14 pt-8 border-t border-white/10 max-w-2xl"
        >
          {t.hero.stats.map((s, i) => (
            <div key={i} data-testid={`hero-stat-${i}`}>
              <div className="font-display font-extrabold text-3xl sm:text-4xl text-[#FF8C42]">{s.value}</div>
              <div className="text-[#9CA3AF] text-sm mt-1">{s.label}</div>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};
